export type TaskDistributionAssignmentRow = {
  storeId?: number | string;
  store_id?: number | string;
  storeName?: string;
  operatorId?: number | string;
  operator_id?: number | string;
  operatorName?: string;
  taskType?: 'POSTING' | 'REMOVAL';
  mediaFormatId?: number | null;
  formatId?: number | null;
  mediaFormatName?: string;
  formatName?: string;
};

export type TaskTypeFilter = 'all' | 'POSTING' | 'REMOVAL';

/** Rows without taskType are treated as posting (legacy sign-off path). */
export function resolveAssignmentTaskType(
  row: TaskDistributionAssignmentRow,
): 'POSTING' | 'REMOVAL' {
  return row.taskType === 'REMOVAL' ? 'REMOVAL' : 'POSTING';
}

export function filterAssignmentsByTaskType<T extends TaskDistributionAssignmentRow>(
  rows: readonly T[],
  filter: TaskTypeFilter,
): T[] {
  if (filter === 'all') return [...rows];
  return rows.filter((row) => resolveAssignmentTaskType(row) === filter);
}

export type RemovalOperatorMismatch = {
  storeId: number;
  storeName: string;
  mediaFormatId: number;
  mediaFormatName: string;
  postingOperatorId: number;
  postingOperatorName: string;
  removalOperatorId: number;
  removalOperatorName: string;
};

function rowStoreId(row: TaskDistributionAssignmentRow): number | null {
  const raw = row.storeId ?? row.store_id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function rowOperatorId(row: TaskDistributionAssignmentRow): number | null {
  const raw = row.operatorId ?? row.operator_id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function rowFormatId(row: TaskDistributionAssignmentRow): number | null {
  const raw = row.mediaFormatId ?? row.formatId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function rowFormatName(row: TaskDistributionAssignmentRow): string {
  return row.mediaFormatName ?? row.formatName ?? 'Unknown format';
}

function formatSlotKey(storeId: number, formatId: number): string {
  return `${storeId}|${formatId}`;
}

function dominantOperator(
  rows: TaskDistributionAssignmentRow[],
): { operatorId: number; operatorName: string } | null {
  const counts = new Map<number, { count: number; name: string }>();
  for (const row of rows) {
    const operatorId = rowOperatorId(row);
    if (operatorId === null) continue;
    const name = row.operatorName ?? `Operator ${operatorId}`;
    const existing = counts.get(operatorId);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(operatorId, { count: 1, name });
    }
  }

  let winner: { operatorId: number; operatorName: string } | null = null;
  let top = 0;
  for (const [operatorId, { count, name }] of counts) {
    if (
      winner === null
      || count > top
      || (count === top && operatorId < winner.operatorId)
    ) {
      winner = { operatorId, operatorName: name };
      top = count;
    }
  }
  return winner;
}

function territorySplitPairKey(
  postingOperatorId: number,
  removalOperatorId: number,
): string {
  return `${postingOperatorId}|${removalOperatorId}`;
}

/**
 * Stores where every format with both posting and removal uses the same
 * posting/removal operator pair — e.g. dedicated posting vs removal staff.
 * Those are intentional territory splits, not dispatch errors.
 */
function filterTerritorySplitStores(
  mismatches: RemovalOperatorMismatch[],
  dualFormatCountByStore: ReadonlyMap<number, number>,
): RemovalOperatorMismatch[] {
  const byStore = new Map<number, RemovalOperatorMismatch[]>();
  for (const mismatch of mismatches) {
    const list = byStore.get(mismatch.storeId);
    if (list) {
      list.push(mismatch);
    } else {
      byStore.set(mismatch.storeId, [mismatch]);
    }
  }

  const suppressedStoreIds = new Set<number>();
  for (const [storeId, storeMismatches] of byStore) {
    const dualFormatCount = dualFormatCountByStore.get(storeId) ?? 0;
    // One format with a split is a real conflict; territory model needs multiple formats.
    if (dualFormatCount < 2 || storeMismatches.length !== dualFormatCount) continue;

    const pairKey = territorySplitPairKey(
      storeMismatches[0]!.postingOperatorId,
      storeMismatches[0]!.removalOperatorId,
    );
    const allSamePair = storeMismatches.every(
      (m) => territorySplitPairKey(m.postingOperatorId, m.removalOperatorId) === pairKey,
    );
    if (allSamePair) {
      suppressedStoreIds.add(storeId);
    }
  }

  if (suppressedStoreIds.size === 0) return mismatches;
  return mismatches.filter((m) => !suppressedStoreIds.has(m.storeId));
}

/**
 * Formats where the dominant POSTING operator ≠ dominant REMOVAL operator
 * at the same store. Different operators on different formats at one store
 * is expected and does not flag. Store-wide consistent posting/removal pairs
 * (territory split) are also suppressed.
 */
export function detectRemovalOperatorMismatches(
  rows: TaskDistributionAssignmentRow[],
): RemovalOperatorMismatch[] {
  type FormatBucket = {
    storeId: number;
    storeName: string;
    formatId: number;
    formatName: string;
    posting: TaskDistributionAssignmentRow[];
    removal: TaskDistributionAssignmentRow[];
  };

  const byFormat = new Map<string, FormatBucket>();

  for (const row of rows) {
    const storeId = rowStoreId(row);
    const formatId = rowFormatId(row);
    if (storeId === null || formatId === null) continue;

    const key = formatSlotKey(storeId, formatId);
    if (!byFormat.has(key)) {
      byFormat.set(key, {
        storeId,
        storeName: row.storeName ?? `Store ${storeId}`,
        formatId,
        formatName: rowFormatName(row),
        posting: [],
        removal: [],
      });
    }

    const bucket = byFormat.get(key)!;
    if (row.taskType === 'REMOVAL') {
      bucket.removal.push(row);
    } else {
      bucket.posting.push(row);
    }
  }

  const dualFormatCountByStore = new Map<number, number>();
  const mismatches: RemovalOperatorMismatch[] = [];
  for (const bucket of byFormat.values()) {
    if (bucket.posting.length === 0 || bucket.removal.length === 0) continue;

    dualFormatCountByStore.set(
      bucket.storeId,
      (dualFormatCountByStore.get(bucket.storeId) ?? 0) + 1,
    );

    const postingDominant = dominantOperator(bucket.posting);
    const removalDominant = dominantOperator(bucket.removal);
    if (!postingDominant || !removalDominant) continue;
    if (postingDominant.operatorId === removalDominant.operatorId) continue;

    mismatches.push({
      storeId: bucket.storeId,
      storeName: bucket.storeName,
      mediaFormatId: bucket.formatId,
      mediaFormatName: bucket.formatName,
      postingOperatorId: postingDominant.operatorId,
      postingOperatorName: postingDominant.operatorName,
      removalOperatorId: removalDominant.operatorId,
      removalOperatorName: removalDominant.operatorName,
    });
  }

  const filtered = filterTerritorySplitStores(mismatches, dualFormatCountByStore);

  return filtered.sort((a, b) => {
    const store = a.storeName.localeCompare(b.storeName);
    if (store !== 0) return store;
    return a.mediaFormatName.localeCompare(b.mediaFormatName);
  });
}

export function countAssignmentsByTaskType(
  assignments: TaskDistributionAssignmentRow[],
): { postingCount: number; removalCount: number } {
  let postingCount = 0;
  let removalCount = 0;
  for (const row of assignments) {
    if (row.taskType === 'REMOVAL') {
      removalCount += 1;
    } else {
      postingCount += 1;
    }
  }
  return { postingCount, removalCount };
}

export function mismatchesForStoreOperator(
  storeId: number,
  operatorId: number,
  mismatches: RemovalOperatorMismatch[],
): RemovalOperatorMismatch[] {
  return mismatches.filter(
    (m) => m.storeId === storeId
      && (m.postingOperatorId === operatorId || m.removalOperatorId === operatorId),
  );
}

/** @deprecated Use mismatchesForStoreOperator — returns first match only. */
export function mismatchForStoreOperator(
  storeId: number,
  operatorId: number,
  mismatches: RemovalOperatorMismatch[],
): RemovalOperatorMismatch | null {
  return mismatchesForStoreOperator(storeId, operatorId, mismatches)[0] ?? null;
}
